import { checkIfNameExists } from '@/components/shared/utils'
import { MAIN_TAB, TAB_TYPE_DEED } from '@/constants'
import { categorizeDocument } from '@/constants/documentTypesRaw'
import { sortByMultiple } from '@/utils'
import { useMemo } from 'react'

export const useGrantorHelpers = runsheet => {
    
    // Grantors from all documents in this runsheet
    const allGrantors = useMemo(() => runsheet?.rdj?.reduce((acc, it) => (
        (it.value)
            ? [...acc, it.value.fullGrantor]
            : acc
    ) ?? [], []), [runsheet])
    
    // Grantees from all documents in this runsheet
    const allGrantees = useMemo(() => runsheet?.rdk?.reduce((acc, it) => (
        (it.value)
            ? [...acc, it.value.fullGrantee]
            : acc
    ) ?? [], []), [runsheet])
    
    // All parties (grantors/grantees) combined, for autocomplete
    const allParties = useMemo(() => [
        ...(allGrantors ?? []),
        ...(allGrantees ?? []),
    ], [allGrantors, allGrantees])
    
    // All parties formatted for chips input
    const grantorGranteeSuggestionList = useMemo(() => (
        allParties.map((gg, idx) => ({ id: idx, name: gg }))
    ), [allParties])
    
    return {
        allGrantors,
        allGrantees,
        allParties,
        grantorGranteeSuggestionList,
    }
    
}

export const buildPartyInterestData = party => {
    if (!party) return {}
    party.surface = party.surface || party.RunsheetDocumentPartiesJoin?.surface
    party.executive = party.executive || party.RunsheetDocumentPartiesJoin?.executive
    party.bonus = party.bonus || party.RunsheetDocumentPartiesJoin?.bonus
    party.delay = party.delay || party.RunsheetDocumentPartiesJoin?.delay
    party.royalty = party.royalty || party.RunsheetDocumentPartiesJoin?.royalty
    return party
}

const removeEmptyNameParties = state => state.filter(s => s?.name?.trim().length > 0)

export const handlePartyChanges = (parties, originalParties) => {
    const filtered = removeEmptyNameParties(parties)
    
    originalParties.forEach(op => {
        const foundParty = filtered.find(f => f.id === op.id)
        
        if (!foundParty) {
            filtered.push({ id: op.id, name: op.name, action: 'remove' })
        } else {
            foundParty.action = foundParty.name !== op.name ? 'update' : 'no-action'
        }
    })
    
    filtered.forEach(f => {
        if (!f.id)
            f.action = 'new'
    })
    
    return filtered
}

export const partiesFromText = (partiesText, originalParties = []) => {
    const filtered = []
    const parties = partiesText.split(';').map(p => p.trim()).filter(p => !!p)
    
    parties.forEach(party => {
        const foundParty = originalParties.find(op => op.name.toLowerCase() === party.toLowerCase())
        
        if (!foundParty) {
            filtered.push({ name: party, action: 'new' })
        } else {
            filtered.push({
                id: foundParty.id,
                name: party,
                action: foundParty.name !== party ? 'update' : 'no-action',
            })
        }
    })
    
    originalParties.forEach(op => {
        const foundParty = filtered.find(f => f.id === op.id)
        
        if (!foundParty) {
            filtered.push({ id: op.id, name: op.name, action: 'remove' })
        }
    })
    
    return filtered
}

export const getRunsheetDocsByTab = (tab, runsheet) => {
    if (tab && tab.id !== MAIN_TAB)
        return (runsheet?.rdj || [])
            .filter(rsjoin => tab.joins
                .some(j => j.runsheetDocumentValueId === rsjoin.runsheetDocumentValueId))
    
    return (runsheet?.rdj || [])
}

export const separateDocumentsByTab = (runsheet, partiesToUniqueMap) => {
    let tabs = []
    
    const documents = sortByMultiple(
        [...(runsheet?.rdj || [])],
        ['value.instrumentDate', 'value.fileDate', 'value.recordingDate'],
        'date',
    )
    
    documents.forEach(doc => {
        const { instrumentType } = doc.value
        const category = categorizeDocument(instrumentType)
        
        doc.category = category
    })
    
    const deeds = documents.filter(doc => doc.category.deed)
    const leases = documents.filter(doc => doc.category.lease)
    let uncategorized = documents.filter(doc => !doc.category.deed && !doc.category.lease)
    
    tabs.push({
        name: 'Deeds',
        documents: deeds,
        type: 'deed',
    })
    
    leases.forEach(lease => leaseFitInTab(tabs, lease, partiesToUniqueMap))
    
    const untabbed = tabs.filter(tab => tab.documents.length === 1
        && !tab.documents[0].value.grantors.some(gtor => deeds.some(deed =>
            deed.value.grantees.some(gtee => partiesToUniqueMap[gtee.id] === partiesToUniqueMap[gtor.id]))))
    
    uncategorized.push(...untabbed.map(tab => tab.documents).flat().filter(doc => !doc.category.deed))
    
    tabs = tabs.filter(tab => !untabbed.some(ut => ut.name === tab.name))
    
    sortByMultiple(
        uncategorized,
        ['value.instrumentDate', 'value.fileDate', 'value.recordingDate'],
        'date',
    )
    
    return { documents, tabs, uncategorized }
    
}

export const leaseFitInTab = (tabs, lease,  partiesToUniqueMap, refreshState = false) => {
    
    // if i find a tab that the document already exists, i dont do anything
    if (tabs.some(tab => tab.documents.some(d => d.id === lease.id))) return
    
    let tabIndex = -1
    let tab = null
    let newData = false
    
    if (refreshState) {
        tabIndex = tabs.findIndex(tab => {
            
            if (tab.type !== 'lease') return false
            
            return tab.documents[tab.documents.length - 1]?.value.grantees
                .some(gtee => lease.value.grantors
                    .some(gtor => partiesToUniqueMap[gtor.id] === partiesToUniqueMap[gtee.id]))
        })
        
        tab = tabs[tabIndex]
    } else {
        tab = tabs.find(tab => {
            
            if (tab.type !== 'lease') return false
            
            return tab.documents[tab.documents.length - 1]?.value.grantees
                .some(gtee => lease.value.grantors
                    .some(gtor => partiesToUniqueMap[gtor.id] === partiesToUniqueMap[gtee.id]))
        })
    }
    
    if (tab) {
        tab.documents.push(lease)
    } else {
        newData = true
        tab = {
            name: checkIfNameExists(tabs, `Lease - ${lease.value.grantees.map(g => g.name).join('; ')}`),
            documents: [lease],
            type: 'lease',
        }
        tabs.push(tab)
    }
    
    if (refreshState && !newData)
        tabs[tabIndex] = { ...tab, documents: [...tab.documents] }
}

export const deedFitInTab = (tabs, document) => {
    const deedTabIdx = tabs.findIndex(st => st.type === TAB_TYPE_DEED)
    const tab = { ...tabs[deedTabIdx] }
    
    if (deedTabIdx > -1 && !tab.documents.some(doc => doc.id === document.id)) {
        tab.documents = sortByMultiple(
            [...tab.documents, document],
            ['value.instrumentDate', 'value.fileDate', 'value.recordingDate'],
            'date')
    }
    
    tabs[deedTabIdx] = tab
}